import { useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { FileDown, Loader2, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTenant } from '@/providers/TenantProvider'
import { usePurchaseOrder, usePurchaseOrderRevision } from '@/hooks/usePurchaseOrders'
import { PoDocument } from '@/components/inventory/purchase-orders/document/PoDocument'
import { downloadPoPdf } from '@/components/inventory/purchase-orders/document/downloadPoPdf'
import { usePoDocumentInputs } from '@/components/inventory/purchase-orders/document/usePoDocumentInputs'

/**
 * `/inventory/purchase-orders/:id/print[?revision=n]` (z8nrz7f7dz).
 *
 * Inside the authenticated routes but outside Layout, so the page is the
 * document alone — no sidebar or top bar to hide from the printer. The one
 * toolbar is `po-no-print`, which PoDocument's print CSS removes.
 *
 * Opened in its own tab, so it waits for the tenant the way Layout does:
 * every request here is tenant-scoped.
 */
export function PurchaseOrderPrintPage() {
  const { isTenantResolved } = useTenant()
  if (!isTenantResolved) return <Centered />
  return <PrintView />
}

function PrintView() {
  const { id = '' } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const revisionParam = params.get('revision')
  const revision = revisionParam && /^\d+$/.test(revisionParam) ? Number(revisionParam) : null

  const current = usePurchaseOrder(id)
  const isOld = revision != null && current.data != null && revision !== current.data.revision
  const snapshot = usePurchaseOrderRevision(id, isOld ? revision : null)
  const po = isOld ? snapshot.data : current.data
  const inputs = usePoDocumentInputs(po)
  const ref = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadError = current.error ?? snapshot.error ?? inputs.error
  if (loadError) {
    return (
      <Centered>
        <p className='text-sm text-destructive'>{loadError instanceof Error ? loadError.message : 'Could not load the order.'}</p>
      </Centered>
    )
  }
  if (!po || !inputs.layout || inputs.isLoading) return <Centered />

  const layout = inputs.layout

  async function pdf() {
    if (!ref.current || !po) return
    setBusy(true)
    setError(null)
    try {
      await downloadPoPdf(ref.current, po.revision > 0 ? `${po.number}-rev${po.revision}` : po.number, layout.document.paperSize)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The PDF could not be made.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className='min-h-screen bg-gray-100 print:bg-white'>
      <div className='po-no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-white px-6 py-3'>
        <p className='text-sm font-medium text-gray-900'>
          {po.number}
          {po.revision > 0 && <span className='ml-2 text-muted-foreground'>Rev {po.revision}</span>}
        </p>
        <div className='flex items-center gap-2'>
          {error && <span className='text-xs text-destructive'>{error}</span>}
          <Button variant='outline' size='sm' className='gap-1' onClick={() => void pdf()} disabled={busy}>
            {busy ? <Loader2 className='h-4 w-4 animate-spin' /> : <FileDown className='h-4 w-4' />}
            Download PDF
          </Button>
          <Button size='sm' className='gap-1' onClick={() => window.print()}>
            <Printer className='h-4 w-4' />
            Print
          </Button>
        </div>
      </div>
      {/* On screen this is the paper preview; printed, the sheet's padding
          gives way to @page margins via the print rules below. */}
      <div className='py-8 print:py-0'>
        <style>{'@media print { .po-print-sheet > .po-document { padding: 0 !important; width: auto !important; min-height: 0 !important; } }'}</style>
        <div className='po-print-sheet'>
          <PoDocument
            ref={ref}
            layout={layout}
            po={po}
            logoUrl={inputs.logoUrl}
            attributes={inputs.attributes}
            watermark={po.status === 'DRAFT' ? 'DRAFT' : undefined}
            mode='preview'
          />
        </div>
      </div>
    </div>
  )
}

function Centered({ children }: { children?: React.ReactNode }) {
  return (
    <div className='flex h-screen items-center justify-center bg-background'>
      {children ?? <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />}
    </div>
  )
}
